import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { customization, i18next, dialogs } from '@yola/ws-sdk';
import { HSLColor } from '@yola/yola-palette-generator';
import dialogTypes from 'src/js/modules/dialogs/constants/dialog-types';
import websiteDesign from 'src/js/modules/website-design';
import textDecorationAttributes from '../constants/text-decoration-attributes';

const {
  components: { ColorSettingsDialog },
  helpers: { parseHslaColorString },
} = websiteDesign;

const createHslaCSSValue = (hslObject) => new HSLColor(...Object.values(hslObject)).toCSSValue();
const createHslCSSValue = (hslObject) =>
  new HSLColor(...Object.values(hslObject)).toCSSValue(false);

const getCaptions = () => ({
  title: i18next.t('Color settings'),
  apply: i18next.t('Save'),
  cancel: i18next.t('Cancel'),
  opacity: i18next.t('Opacity'),
});

const TextDecorationAddColorContainer = ({
  elements,
  premiumAvailable,
  getSharedData,
  resolveSharedData,
}) => {
  const captions = getCaptions();
  const { initialDialogPosition } = getSharedData();

  const colorAttributeDefined = elements[0].hasAttribute(textDecorationAttributes.COLOR);
  const initialColor = elements[0].color.value;

  const dispatch = useDispatch();
  const [dialogPosition, setDialogPosition] = useState({
    x: initialDialogPosition.x,
    y: initialDialogPosition.y,
  });
  const [hslColor, setHslColor] = useState(parseHslaColorString(initialColor));

  const saveSharedData = () => {
    resolveSharedData({
      ...getSharedData(),
      initialDialogPosition: dialogPosition,
    });
  };

  const handleColorChange = ({ hsl }) => {
    setHslColor(hsl);
  };

  const handleColorChangeComplete = ({ hsl }) => {
    const hslaCSSValue = createHslaCSSValue(hsl);

    elements.forEach((element) => {
      element.setAttribute(textDecorationAttributes.COLOR, hslaCSSValue);
    });

    setHslColor(hsl);
  };

  const handleSumbit = () => {
    saveSharedData();
    dispatch(
      customization.actions.addCustomColor({
        value: createHslCSSValue(hslColor),
        opacity: hslColor.a,
      })
    );

    dialogs.operations.show(dialogTypes.TEXT_DECORATION_SETTINGS_DIALOG, {
      elements,
      premiumAvailable,
    });
  };

  const handleCancel = () => {
    if (colorAttributeDefined) {
      elements.forEach((element) => {
        element.setAttribute(textDecorationAttributes.COLOR, initialColor);
      });
    }

    saveSharedData();
    dialogs.operations.show(dialogTypes.TEXT_DECORATION_SETTINGS_DIALOG, {
      elements,
      premiumAvailable,
    });
  };

  const handleDragEnd = (event, position) => {
    setDialogPosition(position);
  };

  return (
    <ColorSettingsDialog
      captions={captions}
      position={{
        offsetX: dialogPosition.x,
        offsetY: dialogPosition.y,
      }}
      color={hslColor}
      overlay="transparent"
      enableAlpha
      displayWarning={false}
      onColorChange={handleColorChange}
      onColorChangeComplete={handleColorChangeComplete}
      onSubmit={handleSumbit}
      onCancel={handleCancel}
      onDragEnd={handleDragEnd}
    />
  );
};

TextDecorationAddColorContainer.propTypes = {
  elements: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.shape(), PropTypes.node])).isRequired,
  premiumAvailable: PropTypes.bool.isRequired,
  getSharedData: PropTypes.func.isRequired,
  resolveSharedData: PropTypes.func.isRequired,
};

export default TextDecorationAddColorContainer;
